import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PlanComparison = ({ currentPlan, onSelectPlan }) => {
  const [billingCycle, setBillingCycle] = useState('monthly');

  const plans = [
    {
      id: 'basic',
      name: 'Basic',
      description: 'Perfect for beginners',
      monthlyPrice: 29,
      yearlyPrice: 290,
      icon: 'Zap',
      color: 'accent',
      features: [
        'Basic AI predictions',
        '5 trading signals/day',
        'Portfolio tracking',
        'Market analysis',
        'Email support',
        'Mobile app access'
      ],
      limitations: [
        'Limited to 3 portfolios',
        'Basic charting tools',
        'Standard support'
      ]
    },
    {
      id: 'premium',
      name: 'Premium',
      description: 'Most popular choice',
      monthlyPrice: 79,
      yearlyPrice: 790,
      icon: 'Star',
      color: 'secondary',
      popular: true,
      features: [
        'Advanced AI predictions',
        '25 trading signals/day',
        'Unlimited portfolios',
        'Advanced market analysis',
        'Real-time alerts',
        'Priority support',
        'Advanced charting',
        'Risk assessment tools',
        'Trading journal',
        'Tax calculation'
      ],
      limitations: [
        'Limited API access',
        'Standard data refresh'
      ]
    },
    {
      id: 'professional',
      name: 'Professional',
      description: 'For serious traders',
      monthlyPrice: 149,
      yearlyPrice: 1490,
      icon: 'Crown',
      color: 'success',
      features: [
        'Premium AI predictions',
        'Unlimited trading signals',
        'Unlimited portfolios',
        'Advanced market analysis',
        'Real-time alerts',
        '24/7 priority support',
        'Professional charting',
        'Advanced risk tools',
        'Trading journal',
        'Tax calculation',
        'API access',
        'Custom indicators',
        'Automated trading',
        'White-label reports'
      ],
      limitations: []
    }
  ];

  const getPrice = (plan) => {
    return billingCycle === 'monthly' ? plan?.monthlyPrice : plan?.yearlyPrice;
  };

  const getSavings = (plan) => {
    if (billingCycle === 'yearly') {
      const monthlyCost = plan?.monthlyPrice * 12;
      const yearlyCost = plan?.yearlyPrice;
      const savings = monthlyCost - yearlyCost;
      return Math.round((savings / monthlyCost) * 100);
    }
    return 0;
  };

  const isCurrentPlan = (planId) => {
    return currentPlan?.type === planId;
  };

  const getButtonText = (planId) => {
    if (isCurrentPlan(planId)) {
      return 'Current Plan';
    }
    const currentPlanIndex = plans?.findIndex(p => p?.id === currentPlan?.type);
    const planIndex = plans?.findIndex(p => p?.id === planId);
    
    if (planIndex > currentPlanIndex) {
      return 'Upgrade';
    } else {
      return 'Downgrade';
    }
  };

  const getButtonVariant = (planId) => {
    if (isCurrentPlan(planId)) {
      return 'outline';
    }
    return 'default';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-card-foreground">Choose Your Plan</h2>
          <p className="text-sm text-muted-foreground">Select the perfect plan for your trading needs</p>
        </div>
        
        <div className="flex items-center bg-muted rounded-lg p-1">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-standard ${
              billingCycle === 'monthly' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-standard ${
              billingCycle === 'yearly' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Yearly
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {plans?.map((plan) => (
          <div
            key={plan?.id}
            className={`relative bg-background border rounded-lg p-6 transition-standard hover:shadow-elevation-2 ${
              plan?.popular ? 'border-primary shadow-elevation-1' : 'border-border'
            } ${isCurrentPlan(plan?.id) ? 'ring-2 ring-success ring-opacity-20' : ''}`}
          >
            {plan?.popular && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-medium">
                  Most Popular
                </span>
              </div>
            )}

            {isCurrentPlan(plan?.id) && (
              <div className="absolute -top-3 right-4">
                <span className="bg-success text-success-foreground px-3 py-1 rounded-full text-xs font-medium">
                  Current
                </span>
              </div>
            )}

            <div className="text-center mb-6">
              <div className={`w-12 h-12 mx-auto mb-4 rounded-lg bg-${plan?.color}/10 flex items-center justify-center`}>
                <Icon name={plan?.icon} size={24} className={`text-${plan?.color}`} />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">{plan?.name}</h3>
              <p className="text-sm text-muted-foreground mb-4">{plan?.description}</p>
              
              <div className="mb-4">
                <div className="flex items-baseline justify-center">
                  <span className="text-3xl font-bold text-foreground">${getPrice(plan)}</span>
                  <span className="text-sm text-muted-foreground ml-1">
                    /{billingCycle === 'monthly' ? 'mo' : 'yr'}
                  </span>
                </div>
                {billingCycle === 'yearly' && getSavings(plan) > 0 && (
                  <p className="text-xs text-success mt-1">
                    Save {getSavings(plan)}% annually
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-3 mb-6">
              {plan?.features?.map((feature, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-foreground">{feature}</span>
                </div>
              ))}
              
              {plan?.limitations?.length > 0 && (
                <div className="pt-3 border-t border-border">
                  {plan?.limitations?.map((limitation, index) => (
                    <div key={index} className="flex items-start space-x-3 mb-2">
                      <Icon name="X" size={16} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{limitation}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button
              variant={getButtonVariant(plan?.id)}
              fullWidth
              disabled={isCurrentPlan(plan?.id)}
              onClick={() => onSelectPlan(plan?.id, billingCycle)}
              iconName={isCurrentPlan(plan?.id) ? 'Check' : 'ArrowRight'}
              iconPosition="right"
            >
              {getButtonText(plan?.id)}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlanComparison;